import { Suspense, useEffect, useRef, useState, lazy, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import useRwas from "../../../hooks/useRwas";
import useCompany from "../../../hooks/useCompany";
import { ClipLoader } from "react-spinners";
import NFTTable from "./components/NFTTable.jsx";
import NFTQRModal from "./components/NFTQRModal.jsx";
import RWAEditParamModal from "./components/RWAEditParamModal.jsx";
import NFTDataModal from "./components/NFTDataModal.jsx";
import RWADataPageEditModal from "./components/RWADataPageEdit.jsx";
import { Cog } from "lucide-react";
import ViewParamField from "./components/ViewParamField.jsx";
import useModal from "../../../hooks/useModal.jsx";
import RequirementsDrawer from "./components/RequirementsDrawer.jsx";
import { Button } from "@oc-labs/wt-storybook";
import ConfirmModal from "../../../components/modals/ConfirmModal.jsx";
import Toggle from "../../../components/Toggle.jsx";
import toast from "react-hot-toast";

export default function RWAView() {
  const { activeCompany: company } = useCompany();
  const { rwaId } = useParams();
  const [rwaInfo, setRwaInfo] = useState(null);
  const [nfts, setNfts] = useState([]);
  const { open } = useModal("rwa-edit-param-modal");
  const { open: openRequirements } = useModal("requirements-drawer");
  const { open: openConfirm } = useModal("confirm-modal");
  const navigate = useNavigate();
  const intervalRef = useRef(null);

  const rwaType = rwaInfo?.type
    ? rwaInfo.type.charAt(0).toUpperCase() + rwaInfo.type.slice(1)
    : "Product";

  const LazyComponent = useMemo(() => {
    return lazy(() =>
      import(`./types/${rwaType}.jsx`).catch(() =>
        import('./types/Product.jsx')
      )
    );
  }, [rwaType]);

  const { getRwaDetails, deleteRWA, getRwaNfts, updateRwa } = useRwas();

  const handleToggleFlag = async (field, value) => {
    if (!company || !rwaInfo) return;
    try {
      await updateRwa(company.id, rwaInfo.id, {
        name: rwaInfo.name,
        type: rwaInfo.type,
        isClaimable: field === "isClaimable" ? value : !!rwaInfo.isClaimable,
        isBuyable: field === "isBuyable" ? value : !!rwaInfo.isBuyable,
      });
      setRwaInfo({ ...rwaInfo, [field]: value });
    } catch (error) {
      console.error(`Error updating ${field}:`, error);
      toast.error(`Failed to update ${field}`);
      await loadRwa();
    }
  };

  const loadRwa = async () => {
    if (!company) return;
    try {
      const data = await getRwaDetails(company.id, rwaId);
      setRwaInfo(data);
    } catch (error) {
      console.error("Error reloading RWA data:", error);
    }
  };

  useEffect(() => {
    loadRwa();
    // eslint-disable-next-line
  }, [rwaId, company]);

  useEffect(() => {
    if (!rwaInfo) return;
    loadNfts();
    // eslint-disable-next-line
  }, [rwaInfo?.id, company?.id]);

  useEffect(() => {
    if (rwaInfo && !rwaInfo.smartContractAddress && company) {
      intervalRef.current = setInterval(() => loadRwa(), 3000);
    }
    return () => clearInterval(intervalRef.current);
    // eslint-disable-next-line
  }, [rwaInfo?.id, rwaInfo?.smartContractAddress, company?.id]);

  const loadNfts = async () => {
    if (!company || !rwaInfo) return;
    try {
      const data = await getRwaNfts(company.id, rwaInfo.id);
      setNfts(data.nfts || []);
    } catch (error) {
      console.error("Error fetching RWA NFTs:", error);
    }
  };

  if(!rwaInfo) return <ClipLoader />

  if (!rwaInfo.smartContractAddress) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] gap-4">
        <ClipLoader size={48} color="#6366f1" />
        <h2 className="text-xl font-semibold text-slate-700">Deploying to Blockchain</h2>
        <p className="text-slate-500 text-sm">This RWA is being deployed to the blockchain. This page will update automatically.</p>
      </div>
    );
  }

  const deleteRwa = async () => {
    await deleteRWA(company.id, rwaInfo.id);
    toast.success("RWA deleted successfully");
    navigate("/dashboard/rwa");
  };

  return (<div>
    <Suspense fallback={<ClipLoader />}>
      <div className="p-4">
        <div className="flex items-center justify-between gap-2 border-b">
          <div className="flex items-center justify-between gap-1">
            <h2 className="text-lg text-gray-900">
              <ViewParamField
                fieldType={rwaInfo?.data?.name?.fieldType}
                value={`${rwaInfo?.name} details`}
                placeholder="Add product name"
                onClick={() => open("name")}
              />
            </h2>
          </div>
          <div className="flex items-center justify-between gap-4 cursor-pointer">
            <div
              className="flex items-center justify-between gap-1 cursor-pointer"
              onClick={() => openRequirements()}
            >
              <Cog className="w-5 h-5 text-gray-700" />
              <p>Requirements</p>
            </div>
            <Button type="danger" onClick={() => {
              openConfirm({
                title: "Delete RWA",
                description: "Are you sure you want to delete this RWA? This action cannot be undone.",
                onConfirm: () => deleteRwa()
              });
            }} size="small">
              Delete RWA
            </Button>
          </div>
        </div>
      </div>
      <div className="px-4 pt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50 border border-slate-200">
          <div className="flex flex-col">
            <span className="text-slate-900 text-sm font-medium">Claimable</span>
            <span className="text-slate-500 text-xs">Users can claim this asset</span>
          </div>
          <Toggle
            checked={!!rwaInfo.isClaimable}
            onChange={(value) => handleToggleFlag("isClaimable", value)}
            text="Claimable"
          />
        </div>
        <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50 border border-slate-200">
          <div className="flex flex-col">
            <span className="text-slate-900 text-sm font-medium">Buyable</span>
            <span className="text-slate-500 text-xs">Users can buy this asset</span>
          </div>
          <Toggle
            checked={!!rwaInfo.isBuyable}
            onChange={(value) => handleToggleFlag("isBuyable", value)}
            text="Buyable"
          />
        </div>
      </div>
      <LazyComponent rwa={rwaInfo} nfts={nfts} reloadRwa={loadRwa} />
      <NFTTable nfts={nfts} reloadNfts={loadNfts} rwaId={rwaInfo?.id} />
      <NFTQRModal />
      <RWAEditParamModal rwa={rwaInfo} reloadRwa={loadRwa} />
      <RWADataPageEditModal rwa={rwaInfo} reloadRwa={loadRwa} />
      <NFTDataModal />
      <RequirementsDrawer rwa={rwaInfo} company={company}/>
      {<ConfirmModal />}
    </Suspense>
  </div>)
}
